import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Input, List, Modal, Tag, Tooltip, Typography } from "antd";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  SearchOutlined,
  BankOutlined,
  PieChartOutlined,
  UserOutlined,
  MessageOutlined,
  CommentOutlined,
  CheckSquareOutlined,
  LinkOutlined,
  LoadingOutlined,
  FlagFilled,
} from "@ant-design/icons";
import { usePost } from "../../hooks/usePost";
import { useGlobalSearch } from "../../store/GlobalSearchProvider";
import {
  EngagementResultItem,
  GlobalSearchRequest,
  GlobalSearchResponseData,
  InteractionResultItem,
  SearchEntityType,
  SearchResultItem,
  TaskResultItem,
  VotingResultItem,
} from "../../types/globalSearchTypes";
import { EngagementType } from "../../models/stewardshipModels";
import { MilestoneKey } from "../../enums/Status";
import { ENGAGEMENT_META_THEME_IMAGES } from "../../pages/CompanyEngagements/constants";
import m1Icon from "../../assets/svg/m1_icon.svg";
import m2Icon from "../../assets/svg/m2_icon.svg";
import m3Icon from "../../assets/svg/m3_icon.svg";
import m4Icon from "../../assets/svg/m4_icon.svg";
import ciIcon from "../../assets/svg/ci_icon.svg";
import {
  compassBordeaux,
  compassLightGreen,
  compassLightGreenBackground,
  compassLightGreyBackground,
  compassLightRed,
  compassYellow,
  compassYellowBackground,
  darkGreen,
  greyText,
  lightGreyOutline,
} from "../../styles";
import { ROUTES } from "../../routes/routeNames";
import { toSlug } from "../../utils/formattingUtils";
import { Avatar } from "../visuals/Avatar";

const { Text } = Typography;

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 25;

const TYPE_ORDER: SearchEntityType[] = [
  SearchEntityType.COMPANY,
  SearchEntityType.FUND,
  SearchEntityType.MANAGER,
  SearchEntityType.ENGAGEMENT,
  SearchEntityType.INTERACTION,
  SearchEntityType.VOTING,
  SearchEntityType.LINKAGE,
  SearchEntityType.TASK,
];

const TYPE_LABEL: Record<SearchEntityType, string> = {
  [SearchEntityType.COMPANY]: "Companies",
  [SearchEntityType.FUND]: "Funds",
  [SearchEntityType.MANAGER]: "Managers",
  [SearchEntityType.ENGAGEMENT]: "Engagements",
  [SearchEntityType.INTERACTION]: "Interactions",
  [SearchEntityType.VOTING]: "Voting records",
  [SearchEntityType.LINKAGE]: "Linkages",
  [SearchEntityType.TASK]: "Tasks",
};

const TYPE_ICON: Record<SearchEntityType, React.ElementType> = {
  [SearchEntityType.COMPANY]: BankOutlined,
  [SearchEntityType.FUND]: PieChartOutlined,
  [SearchEntityType.MANAGER]: UserOutlined,
  [SearchEntityType.ENGAGEMENT]: MessageOutlined,
  [SearchEntityType.INTERACTION]: CommentOutlined,
  [SearchEntityType.VOTING]: CheckSquareOutlined,
  [SearchEntityType.LINKAGE]: LinkOutlined,
  [SearchEntityType.TASK]: CheckSquareOutlined,
};

/* ------------------------------------------------------------------ */
/*  Navigation — one place, easy to extend                             */
/* ------------------------------------------------------------------ */

const votingHref = (v: VotingResultItem): string | null =>
  v.company_id
    ? `${ROUTES.COMPANY_PROFILE}/${v.company_id}?votingRecordId=${v.id}`
    : null;

const resolveHref = (item: SearchResultItem): string | null => {
  switch (item.type) {
    case SearchEntityType.COMPANY:
      return `${ROUTES.COMPANY_PROFILE}/${item.id}`;
    case SearchEntityType.FUND:
      return `${ROUTES.SCREENING_TOOL}/${toSlug(item.label)}`;
    case SearchEntityType.MANAGER:
      return ROUTES.STEWARDSHIP;
    case SearchEntityType.ENGAGEMENT:
      return `${ROUTES.SINGLE_ENGAGEMENT}/${item.id}`;
    case SearchEntityType.INTERACTION:
      // No dedicated interaction page — open the parent engagement.
      return `${ROUTES.SINGLE_ENGAGEMENT}/${item.engagement_record_id}`;
    case SearchEntityType.VOTING:
      return votingHref(item);
    case SearchEntityType.LINKAGE:
      return ROUTES.LINKAGE_DASHBOARD;
    case SearchEntityType.TASK:
      return `${ROUTES.WORKSPACE}/view-task?taskId=${item.id}`;
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ */
/*  Styled bits                                                        */
/* ------------------------------------------------------------------ */

const SearchWrap = styled.div`
  padding: 14px 16px 8px;
  border-bottom: 1px solid ${lightGreyOutline};
`;

const GroupHeader = styled.div`
  padding: 10px 18px 4px;
  font-family: "ApercuMedium";
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${greyText};
`;

const Row = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 18px;
  cursor: ${({ $disabled }): string => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }): string => ($disabled ? "0.55" : "1")};
  transition: background-color 80ms ease;
  background-color: ${({ $active }): string =>
    $active ? compassLightGreenBackground : "transparent"};
  border-left: 2px solid
    ${({ $active }): string => ($active ? compassLightGreen : "transparent")};

  &:hover {
    background-color: ${({ $disabled }): string =>
      $disabled ? "transparent" : compassLightGreenBackground};
  }
`;

const RowBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const AccessoriesWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const MainAvatarTile = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 6px;
  background: ${compassLightGreyBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: ${darkGreen};
  font-size: 15px;
`;

const MetaThemeMark = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
`;

const MilestoneMark = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: block;
`;

// Same mapping used in CompanyProfile/EngagementTable — keep them in sync.
const MILESTONE_ICONS: Record<MilestoneKey, string> = {
  [MilestoneKey.M1]: m4Icon,
  [MilestoneKey.M2]: m3Icon,
  [MilestoneKey.M3]: m2Icon,
  [MilestoneKey.M4]: m1Icon,
  [MilestoneKey.CI]: ciIcon,
};

const ResultsScroll = styled.div`
  max-height: 55vh;
  overflow-y: auto;
  padding: 4px 0 6px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  border-top: 1px solid ${lightGreyOutline};
  background: ${compassLightGreyBackground};
  color: ${greyText};
  font-size: 11.5px;
`;

const EmptyState = styled.div`
  padding: 28px 18px;
  text-align: center;
  color: ${greyText};
  font-size: 13px;
`;

const kbdStyle: React.CSSProperties = {
  fontFamily: "ApercuMono, Apercu, monospace",
  fontSize: 11,
  color: greyText,
  background: "white",
  border: `1px solid ${lightGreyOutline}`,
  borderRadius: 4,
  padding: "1px 6px",
};

const matchedTagStyle: React.CSSProperties = {
  marginRight: 0,
  fontFamily: "ApercuMono, Apercu, monospace",
  fontSize: 10.5,
  color: greyText,
  background: "white",
  borderColor: lightGreyOutline,
};

/* ------------------------------------------------------------------ */
/*  Rendering helpers                                                  */
/* ------------------------------------------------------------------ */

/** Bare meta-theme mark — no background, just the icon. */
const MetaThemeIcon: React.FC<{ metaTheme: string | null }> = ({
  metaTheme,
}) => {
  const key =
    (metaTheme as EngagementType) || EngagementType.StrategyRiskCommunication;
  const themeCfg =
    ENGAGEMENT_META_THEME_IMAGES[key] ||
    ENGAGEMENT_META_THEME_IMAGES[EngagementType.StrategyRiskCommunication];
  return (
    <Tooltip title={metaTheme || "Strategy"}>
      <MetaThemeMark src={themeCfg.logo} alt="" />
    </Tooltip>
  );
};

/** Bare milestone mark — same icon set used in the CompanyProfile table. */
const MilestoneIcon: React.FC<{ milestone: string | null }> = ({
  milestone,
}) => {
  if (!milestone) return null;
  const key = milestone.toUpperCase() as MilestoneKey;
  const src = MILESTONE_ICONS[key];
  if (!src) return null;
  return (
    <Tooltip title={milestone}>
      <MilestoneMark src={src} alt="" />
    </Tooltip>
  );
};

/** Tiny circular avatar for the owning manager, only when we have data. */
const ManagerAvatar: React.FC<{
  managerName: string | null;
  managerImage: string | null;
}> = ({ managerName, managerImage }) => {
  if (!managerName) return null;
  return (
    <Avatar username={managerName} image={managerImage} size="small" tooltip />
  );
};

/**
 * The main 32px tile shown to the left of every row. Companies + funds +
 * engagements + interactions + voting + linkages all have `image_url` which
 * is the company logo — use it when present, otherwise fall back to the type
 * icon. Managers use their `image_url` (headshot) if present, otherwise the
 * shared initial-based Avatar for a consistent look with the NavBar.
 */
const MainAvatar: React.FC<{ item: SearchResultItem }> = ({ item }) => {
  const Icon = TYPE_ICON[item.type];

  if (item.type === SearchEntityType.MANAGER && !item.image_url) {
    return <Avatar username={item.label} size="default" />;
  }

  if (item.type === SearchEntityType.TASK) {
    // Tasks have no image; flag when flagged, otherwise the type icon.
    return (
      <MainAvatarTile>
        {item.is_flag ? (
          <FlagFilled style={{ color: compassBordeaux, fontSize: 15 }} />
        ) : (
          <Icon />
        )}
      </MainAvatarTile>
    );
  }

  return (
    <MainAvatarTile>
      {item.image_url ? (
        <img
          src={item.image_url}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Icon />
      )}
    </MainAvatarTile>
  );
};

const priorityTagColour = (
  priority: string | null,
): { bg: string; fg: string } => {
  const p = (priority || "").toLowerCase();
  if (p.includes("high") || p.includes("urgent")) {
    return { bg: compassLightRed, fg: compassBordeaux };
  }
  if (p.includes("med")) {
    return { bg: compassYellowBackground, fg: compassYellow };
  }
  return { bg: compassLightGreyBackground, fg: greyText };
};

/** Type-specific chips/avatars shown right of the label/subtitle. */
const RowAccessories: React.FC<{ item: SearchResultItem }> = ({ item }) => {
  const nodes: React.ReactNode[] = [];

  if (
    item.type === SearchEntityType.ENGAGEMENT ||
    item.type === SearchEntityType.INTERACTION
  ) {
    const withTheme = item as EngagementResultItem | InteractionResultItem;
    nodes.push(<MetaThemeIcon key="meta" metaTheme={withTheme.meta_theme} />);
    if (item.type === SearchEntityType.INTERACTION) {
      nodes.push(
        <MilestoneIcon
          key="ms"
          milestone={(item as InteractionResultItem).engagement_milestone}
        />,
      );
    }
    nodes.push(
      <ManagerAvatar
        key="mgr"
        managerName={withTheme.manager_name}
        managerImage={withTheme.manager_image}
      />,
    );
  } else if (item.type === SearchEntityType.VOTING) {
    const v = item as VotingResultItem;
    nodes.push(
      <ManagerAvatar
        key="mgr"
        managerName={v.manager_name}
        managerImage={v.manager_image}
      />,
    );
  } else if (item.type === SearchEntityType.TASK) {
    const t = item as TaskResultItem;
    if (t.status) {
      nodes.push(
        <Tag
          key="status"
          style={{
            marginRight: 0,
            fontSize: 10.5,
            color: greyText,
            background: compassLightGreyBackground,
            borderColor: lightGreyOutline,
          }}
        >
          {t.status}
        </Tag>,
      );
    }
    if (t.priority) {
      const { bg, fg } = priorityTagColour(t.priority);
      nodes.push(
        <Tag
          key="prio"
          style={{
            marginRight: 0,
            fontSize: 10.5,
            color: fg,
            background: bg,
            borderColor: "transparent",
          }}
        >
          {t.priority}
        </Tag>,
      );
    }
  }

  if (item.matched_field) {
    nodes.push(
      <Tag key="matched" style={matchedTagStyle}>
        {item.matched_field}
      </Tag>,
    );
  }

  return <AccessoriesWrap>{nodes}</AccessoriesWrap>;
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface GroupedResults {
  type: SearchEntityType;
  items: SearchResultItem[];
}

const groupResults = (items: SearchResultItem[]): GroupedResults[] => {
  const byType = new Map<SearchEntityType, SearchResultItem[]>();
  for (const item of items) {
    const bucket = byType.get(item.type);
    if (bucket) bucket.push(item);
    else byType.set(item.type, [item]);
  }
  return TYPE_ORDER.filter((t) => byType.has(t)).map((t) => ({
    type: t,
    items: byType.get(t) as SearchResultItem[],
  }));
};

const flattenGroups = (groups: GroupedResults[]): SearchResultItem[] =>
  groups.flatMap((g) => g.items);

export const GlobalSearchPalette: React.FC = () => {
  const { isOpen, close } = useGlobalSearch();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const activeRowRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const { data, isLoading, sendData } = usePost<
    GlobalSearchResponseData,
    GlobalSearchRequest
  >();

  /* -- reset on close ---------------------------------------------- */
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setDebounced("");
      setActiveIndex(0);
    }
  }, [isOpen]);

  /* -- autofocus when opened --------------------------------------- */
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return (): void => window.clearTimeout(t);
  }, [isOpen]);

  /* -- debounce query --------------------------------------------- */
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return (): void => window.clearTimeout(t);
  }, [query]);

  /* -- fetch on debounced-query change ---------------------------- */
  useEffect(() => {
    if (!isOpen) return;
    if (debounced.length < MIN_QUERY_LENGTH) return;
    sendData("GET_GLOBAL_SEARCH", { query: debounced, limit: RESULT_LIMIT });
    setActiveIndex(0);
  }, [debounced, isOpen, sendData]);

  const groups = useMemo(
    () => groupResults(data?.results ?? []),
    [data?.results],
  );
  const flat = useMemo(() => flattenGroups(groups), [groups]);

  /* -- scroll active row into view -------------------------------- */
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const openItem = useCallback(
    (item: SearchResultItem) => {
      const href = resolveHref(item);
      if (!href) return;
      close();
      navigate(href);
    },
    [close, navigate],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (flat.length ? (i + 1) % flat.length : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) =>
          flat.length ? (i - 1 + flat.length) % flat.length : 0,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flat[activeIndex];
        if (item) openItem(item);
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    },
    [flat, activeIndex, openItem, close],
  );

  /* -- body ------------------------------------------------------- */
  let body: React.ReactNode;
  if (debounced.length < MIN_QUERY_LENGTH) {
    body = (
      <EmptyState>
        Type at least {MIN_QUERY_LENGTH} characters to search across companies,
        funds, managers, engagements, interactions, votes, linkages and tasks.
      </EmptyState>
    );
  } else if (isLoading && !flat.length) {
    body = (
      <EmptyState>
        <LoadingOutlined style={{ marginRight: 8 }} />
        Searching…
      </EmptyState>
    );
  } else if (!flat.length) {
    body = <EmptyState>No results for &ldquo;{debounced}&rdquo;</EmptyState>;
  } else {
    let cursor = 0;
    body = groups.map((group) => {
      const startIndex = cursor;
      cursor += group.items.length;
      return (
        <div key={group.type}>
          <GroupHeader>{TYPE_LABEL[group.type]}</GroupHeader>
          <List
            dataSource={group.items}
            split={false}
            renderItem={(item, i): React.ReactNode => {
              const globalIndex = startIndex + i;
              const isActive = globalIndex === activeIndex;
              const disabled = resolveHref(item) === null;
              return (
                <Row
                  key={`${item.type}-${item.id}`}
                  $active={isActive}
                  $disabled={disabled}
                  ref={isActive ? activeRowRef : undefined}
                  onMouseEnter={(): void => setActiveIndex(globalIndex)}
                  onClick={(): void => openItem(item)}
                >
                  <MainAvatar item={item} />
                  <RowBody>
                    <Text
                      ellipsis
                      style={{
                        color: darkGreen,
                        fontFamily: "ApercuMedium",
                        fontSize: 14,
                      }}
                    >
                      {item.label}
                    </Text>
                    {item.subtitle && (
                      <Text ellipsis type="secondary" style={{ fontSize: 12 }}>
                        {item.subtitle}
                      </Text>
                    )}
                  </RowBody>
                  <RowAccessories item={item} />
                </Row>
              );
            }}
          />
        </div>
      );
    });
  }

  const searchingLive = isLoading && debounced.length >= MIN_QUERY_LENGTH;

  return (
    <Modal
      open={isOpen}
      onCancel={close}
      footer={null}
      closable={false}
      centered={false}
      destroyOnClose
      width={680}
      maskClosable
      style={{ top: "12vh" }}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 10, overflow: "hidden" },
      }}
    >
      <SearchWrap>
        <Input
          ref={inputRef as React.RefObject<any>} // eslint-disable-line @typescript-eslint/no-explicit-any
          size="large"
          variant="borderless"
          autoFocus
          placeholder="Search companies, funds, engagements, votes…"
          prefix={
            searchingLive ? (
              <LoadingOutlined style={{ color: greyText }} />
            ) : (
              <SearchOutlined style={{ color: greyText }} />
            )
          }
          suffix={<span style={kbdStyle}>esc</span>}
          value={query}
          onChange={(e): void => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          autoComplete="off"
        />
      </SearchWrap>
      <ResultsScroll>{body}</ResultsScroll>
      <Footer>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{ display: "inline-flex", gap: 4, alignItems: "center" }}
          >
            <span style={kbdStyle}>↑</span>
            <span style={kbdStyle}>↓</span>
            navigate
          </span>
          <span
            style={{ display: "inline-flex", gap: 4, alignItems: "center" }}
          >
            <span style={kbdStyle}>↵</span>
            open
          </span>
        </div>
        {data?.total !== undefined && debounced.length >= MIN_QUERY_LENGTH && (
          <span>
            {data.total} result{data.total === 1 ? "" : "s"}
          </span>
        )}
      </Footer>
    </Modal>
  );
};
